import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestmentSection = ({ data, onContactClick, onCallClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null); // 'success' | 'error' | null

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    setSubscribeStatus(null);

    try {
      await axios.post(`${API_URL}/api/subscribe`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(null), 5000);
    } catch (error) {
      console.error('Subscribe error:', error);
      setSubscribeStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} className="bg-[#111111] py-20 lg:py-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="text-[#C8102E] text-base md:text-lg tracking-[0.2em] font-medium uppercase mb-8">
            {data.label}
          </div>
          
          <h2
            className="text-white font-bold text-4xl md:text-5xl lg:text-6xl mb-6"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.headline}
          </h2>

          <p
            className="text-[#F0EBE3] text-lg leading-relaxed mb-12 max-w-2xl mx-auto"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.body}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={onContactClick}
              className="bg-[#C8102E] text-white px-10 py-4 rounded-full font-medium hover:bg-[#A00D25] transition-all duration-300 shadow-lg hover:shadow-[#C8102E]/50"
            >
              Pitch Deck Talep Et →
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={onCallClick}
              className="border-2 border-white text-white px-10 py-4 rounded-full font-medium hover:bg-white hover:text-[#0A0A0A] transition-all duration-300"
            >
              Görüşme Ayarla
            </motion.button>
          </div>

          <p
            className="text-[#F0EBE3]/40 text-sm mb-16"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.footer}
          </p>

          {/* Email Subscription Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="border-t border-[#2A2A2A] pt-12"
          >
            <h3
              className="text-white font-bold text-2xl md:text-3xl mb-4"
              style={{ fontFamily: "'Ubuntu', sans-serif" }}
            >
              Gelişmelerden haberdar ol
            </h3>
            <p
              className="text-[#F0EBE3]/60 text-base mb-6"
              style={{ fontFamily: "'Ubuntu', sans-serif" }}
            >
              SEFER'den haberler ve güncellemeler için e-posta listesine katıl.
            </p>

            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F0EBE3]/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-posta adresiniz"
                  required
                  disabled={isSubmitting || subscribeStatus === 'success'}
                  className="w-full bg-[#1A1A1A] text-white border border-[#2A2A2A] rounded-full pl-12 pr-4 py-3 focus:outline-none focus:border-[#C8102E] transition-colors duration-300 disabled:opacity-50"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                />
              </div>
              <motion.button
                type="submit"
                disabled={isSubmitting || subscribeStatus === 'success'}
                whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                  subscribeStatus === 'success'
                    ? 'bg-green-600 text-white'
                    : 'bg-[#C8102E] text-white hover:bg-[#A00D25]'
                } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : subscribeStatus === 'success' ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Kaydedildi
                  </>
                ) : (
                  'Abone Ol'
                )}
              </motion.button>
            </form>

            {subscribeStatus === 'error' && (
              <p className="text-red-500 text-sm mt-3">Bir hata oluştu. Lütfen tekrar deneyin.</p>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentSection;
